/**
 * Showcase Fixtures - Fictional "Fernbrook Ltd" data for the demo showcase.
 *
 * All IDs are prefixed with "sc-" for easy identification.
 * Data is seeded into the OfflineStore so the app runs entirely offline.
 */

import type { OfflineStore } from './offline-store'

const ORG_ID = 'sc-org-001'
const USER_ID = 'sc-user-001'
const NOW = new Date().toISOString()

// ─── User ──────────────────────────────────────────────────

const SHOWCASE_USER = {
  id: USER_ID,
  email: 'alex@fernbrook.example',
  name: 'Alex Morgan',
  role: 'admin' as const,
  membership_role: 'owner' as const,
  onboarded: false,
  active: true,
  last_login_at: NOW,
  created_at: '2024-01-15T00:00:00Z',
  memberships: [
    {
      organisation_id: ORG_ID,
      organisation_name: 'Fernbrook Ltd',
      organisation_slug: 'fernbrook',
      role: 'owner' as const,
      personal: true,
    },
  ],
  organisation: { id: ORG_ID, name: 'Fernbrook Ltd', slug: 'fernbrook' },
  effective_ui_mode: (localStorage.getItem('ledger:ui-mode') || 'standard') as 'standard' | 'classic',
  graduated_tabs: [],
}

// ─── Organisation ──────────────────────────────────────────

const SHOWCASE_ORG = {
  id: ORG_ID,
  name: 'Fernbrook Ltd',
  slug: 'fernbrook',
  default_currency: 'AUD',
  tax_label: 'GST',
  personal: true,
  default: true,
  suspended: false,
  chart_template: 'generic',
  created_at: '2024-01-15T00:00:00Z',
  updated_at: NOW,
  accounts_count: 15,
  system_accounts_count: 3,
  fully_provisioned: true,
}

// ─── Accounts ──────────────────────────────────────────────

const SHOWCASE_ACCOUNTS = [
  { id: 'sc-acc-001', code: '1-1000', name: 'Cash at Bank', account_type: 'asset', normal_balance: 'debit', balance_policy: 'unrestricted', allow_negative: false, currency: 'AUD', system_account: false, active: true, created_at: '2024-01-15T00:00:00Z' },
  { id: 'sc-acc-002', code: '1-1100', name: 'Accounts Receivable', account_type: 'asset', normal_balance: 'debit', balance_policy: 'unrestricted', allow_negative: false, currency: 'AUD', system_account: true, active: true, created_at: '2024-01-15T00:00:00Z' },
  { id: 'sc-acc-003', code: '1-1200', name: 'Petty Cash', account_type: 'asset', normal_balance: 'debit', balance_policy: 'unrestricted', allow_negative: false, currency: 'AUD', system_account: false, active: true, created_at: '2024-01-15T00:00:00Z' },
  { id: 'sc-acc-004', code: '2-1000', name: 'Accounts Payable', account_type: 'liability', normal_balance: 'credit', balance_policy: 'unrestricted', allow_negative: false, currency: 'AUD', system_account: true, active: true, created_at: '2024-01-15T00:00:00Z' },
  { id: 'sc-acc-005', code: '2-1100', name: 'GST Collected', account_type: 'liability', normal_balance: 'credit', balance_policy: 'unrestricted', allow_negative: true, currency: 'AUD', system_account: true, active: true, created_at: '2024-01-15T00:00:00Z' },
  { id: 'sc-acc-006', code: '2-1200', name: 'GST Paid', account_type: 'liability', normal_balance: 'debit', balance_policy: 'unrestricted', allow_negative: true, currency: 'AUD', system_account: false, active: true, created_at: '2024-01-15T00:00:00Z' },
  { id: 'sc-acc-007', code: '3-1000', name: 'Owner\'s Equity', account_type: 'equity', normal_balance: 'credit', balance_policy: 'unrestricted', allow_negative: false, currency: 'AUD', system_account: false, active: true, created_at: '2024-01-15T00:00:00Z' },
  { id: 'sc-acc-008', code: '3-1100', name: 'Retained Earnings', account_type: 'equity', normal_balance: 'credit', balance_policy: 'unrestricted', allow_negative: true, currency: 'AUD', system_account: false, active: true, created_at: '2024-01-15T00:00:00Z' },
  { id: 'sc-acc-009', code: '4-1000', name: 'Sales Revenue', account_type: 'revenue', normal_balance: 'credit', balance_policy: 'unrestricted', allow_negative: false, currency: 'AUD', system_account: false, active: true, created_at: '2024-01-15T00:00:00Z' },
  { id: 'sc-acc-010', code: '4-1100', name: 'Consulting Income', account_type: 'revenue', normal_balance: 'credit', balance_policy: 'unrestricted', allow_negative: false, currency: 'AUD', system_account: false, active: true, created_at: '2024-01-15T00:00:00Z' },
  { id: 'sc-acc-011', code: '5-1000', name: 'Office Supplies', account_type: 'expense', normal_balance: 'debit', balance_policy: 'unrestricted', allow_negative: false, currency: 'AUD', system_account: false, active: true, created_at: '2024-01-15T00:00:00Z' },
  { id: 'sc-acc-012', code: '5-1100', name: 'Rent', account_type: 'expense', normal_balance: 'debit', balance_policy: 'unrestricted', allow_negative: false, currency: 'AUD', system_account: false, active: true, created_at: '2024-01-15T00:00:00Z' },
  { id: 'sc-acc-013', code: '5-1200', name: 'Utilities', account_type: 'expense', normal_balance: 'debit', balance_policy: 'unrestricted', allow_negative: false, currency: 'AUD', system_account: false, active: true, created_at: '2024-01-15T00:00:00Z' },
  { id: 'sc-acc-014', code: '5-1300', name: 'Travel & Entertainment', account_type: 'expense', normal_balance: 'debit', balance_policy: 'unrestricted', allow_negative: false, currency: 'AUD', system_account: false, active: true, created_at: '2024-01-15T00:00:00Z' },
  { id: 'sc-acc-015', code: '5-1400', name: 'Professional Services', account_type: 'expense', normal_balance: 'debit', balance_policy: 'unrestricted', allow_negative: false, currency: 'AUD', system_account: false, active: true, created_at: '2024-01-15T00:00:00Z' },
]

// ─── Journal Entries ───────────────────────────────────────

const SHOWCASE_JOURNALS = [
  {
    id: 'sc-je-001', description: 'Owner capital contribution', reference: 'JE-001', state: 'posted',
    posted_at: '2024-02-01T09:00:00Z', voided_at: null, void_reason: null, signed: false,
    created_at: '2024-02-01T09:00:00Z', updated_at: '2024-02-01T09:00:00Z', balanced: true,
    entry_lines: [
      { id: 'sc-el-001', account_id: 'sc-acc-001', account_code: '1-1000', account_name: 'Cash at Bank', direction: 'debit', amount: '50000.00', currency: 'AUD' },
      { id: 'sc-el-002', account_id: 'sc-acc-007', account_code: '3-1000', account_name: "Owner's Equity", direction: 'credit', amount: '50000.00', currency: 'AUD' },
    ],
  },
  {
    id: 'sc-je-002', description: 'Monthly rent payment', reference: 'JE-002', state: 'posted',
    posted_at: '2024-02-05T10:00:00Z', voided_at: null, void_reason: null, signed: false,
    created_at: '2024-02-05T10:00:00Z', updated_at: '2024-02-05T10:00:00Z', balanced: true,
    entry_lines: [
      { id: 'sc-el-003', account_id: 'sc-acc-012', account_code: '5-1100', account_name: 'Rent', direction: 'debit', amount: '3200.00', currency: 'AUD' },
      { id: 'sc-el-004', account_id: 'sc-acc-001', account_code: '1-1000', account_name: 'Cash at Bank', direction: 'credit', amount: '3200.00', currency: 'AUD' },
    ],
  },
  {
    id: 'sc-je-003', description: 'Consulting invoice #1042', reference: 'JE-003', state: 'posted',
    posted_at: '2024-02-10T14:00:00Z', voided_at: null, void_reason: null, signed: false,
    created_at: '2024-02-10T14:00:00Z', updated_at: '2024-02-10T14:00:00Z', balanced: true,
    entry_lines: [
      { id: 'sc-el-005', account_id: 'sc-acc-002', account_code: '1-1100', account_name: 'Accounts Receivable', direction: 'debit', amount: '5500.00', currency: 'AUD' },
      { id: 'sc-el-006', account_id: 'sc-acc-005', account_code: '2-1100', account_name: 'GST Collected', direction: 'credit', amount: '500.00', currency: 'AUD' },
      { id: 'sc-el-007', account_id: 'sc-acc-010', account_code: '4-1100', account_name: 'Consulting Income', direction: 'credit', amount: '5000.00', currency: 'AUD' },
    ],
  },
  {
    id: 'sc-je-004', description: 'Office supplies purchase', reference: 'JE-004', state: 'posted',
    posted_at: '2024-02-15T11:30:00Z', voided_at: null, void_reason: null, signed: false,
    created_at: '2024-02-15T11:30:00Z', updated_at: '2024-02-15T11:30:00Z', balanced: true,
    entry_lines: [
      { id: 'sc-el-008', account_id: 'sc-acc-011', account_code: '5-1000', account_name: 'Office Supplies', direction: 'debit', amount: '385.00', currency: 'AUD' },
      { id: 'sc-el-009', account_id: 'sc-acc-006', account_code: '2-1200', account_name: 'GST Paid', direction: 'debit', amount: '35.00', currency: 'AUD' },
      { id: 'sc-el-010', account_id: 'sc-acc-001', account_code: '1-1000', account_name: 'Cash at Bank', direction: 'credit', amount: '420.00', currency: 'AUD' },
    ],
  },
  {
    id: 'sc-je-005', description: 'Client payment received', reference: 'JE-005', state: 'posted',
    posted_at: '2024-02-20T16:00:00Z', voided_at: null, void_reason: null, signed: false,
    created_at: '2024-02-20T16:00:00Z', updated_at: '2024-02-20T16:00:00Z', balanced: true,
    entry_lines: [
      { id: 'sc-el-011', account_id: 'sc-acc-001', account_code: '1-1000', account_name: 'Cash at Bank', direction: 'debit', amount: '5500.00', currency: 'AUD' },
      { id: 'sc-el-012', account_id: 'sc-acc-002', account_code: '1-1100', account_name: 'Accounts Receivable', direction: 'credit', amount: '5500.00', currency: 'AUD' },
    ],
  },
]

// ─── Contacts (Customers + Suppliers) ──────────────────────

const SHOWCASE_CUSTOMERS = [
  { id: 'sc-cust-001', name: 'Oakridge Developments', email: 'accounts@oakridge.example', phone: '03 9555 1234', address: null, address_line_1: null, address_line_2: null, city: null, region: null, postal_code: null, country: 'AU', tax_id: null, delivery_address_line_1: null, delivery_address_line_2: null, delivery_city: null, delivery_region: null, delivery_postal_code: null, delivery_country: null, has_delivery_address: false, portal_active: false, metadata: {}, invoices_count: 1, currency: 'AUD', active: true, created_at: '2024-01-20T00:00:00Z' },
  { id: 'sc-cust-002', name: 'Summit Advisory Group', email: 'billing@summit.example', phone: '02 8800 5678', address: null, address_line_1: null, address_line_2: null, city: null, region: null, postal_code: null, country: 'AU', tax_id: null, delivery_address_line_1: null, delivery_address_line_2: null, delivery_city: null, delivery_region: null, delivery_postal_code: null, delivery_country: null, has_delivery_address: false, portal_active: false, metadata: {}, invoices_count: 1, currency: 'AUD', active: true, created_at: '2024-01-22T00:00:00Z' },
  { id: 'sc-cust-003', name: 'Coastal Property Partners', email: 'finance@coastal.example', phone: '07 3200 9012', address: null, address_line_1: null, address_line_2: null, city: null, region: null, postal_code: null, country: 'AU', tax_id: null, delivery_address_line_1: null, delivery_address_line_2: null, delivery_city: null, delivery_region: null, delivery_postal_code: null, delivery_country: null, has_delivery_address: false, portal_active: false, metadata: {}, invoices_count: 1, currency: 'AUD', active: true, created_at: '2024-02-01T00:00:00Z' },
]

const SHOWCASE_SUPPLIERS = [
  { id: 'sc-sup-001', name: 'Metro Office Supplies', email: 'orders@metro.example', phone: '03 9111 2222', tax_id: null, currency: 'AUD', address_line_1: null, city: null, country: 'AU', active: true, metadata: {}, created_at: '2024-01-18T00:00:00Z' },
  { id: 'sc-sup-002', name: 'Pacific Utilities Co', email: 'accounts@pacific-util.example', phone: '1300 555 789', tax_id: null, currency: 'AUD', address_line_1: null, city: null, country: 'AU', active: true, metadata: {}, created_at: '2024-01-18T00:00:00Z' },
]

// ─── Invoices ──────────────────────────────────────────────

const SHOWCASE_INVOICES = [
  { id: 'sc-inv-001', invoice_number: 'INV-1042', invoice_type: 'standard', status: 'sent', currency: 'AUD', subtotal_cents: 500000, tax_cents: 50000, credit_applied_cents: 0, total_cents: 550000, period_start: '2024-02-01', period_end: '2024-02-28', issued_at: '2024-02-10T14:00:00Z', due_at: '2024-03-10T00:00:00Z', paid_at: null, customer_id: 'sc-cust-002', has_pdf: false, created_at: '2024-02-10T14:00:00Z', metadata: null },
  { id: 'sc-inv-002', invoice_number: 'INV-1043', invoice_type: 'standard', status: 'paid', currency: 'AUD', subtotal_cents: 800000, tax_cents: 80000, credit_applied_cents: 0, total_cents: 880000, period_start: '2024-02-01', period_end: '2024-02-28', issued_at: '2024-02-15T09:00:00Z', due_at: '2024-03-15T00:00:00Z', paid_at: '2024-02-20T16:00:00Z', customer_id: 'sc-cust-001', has_pdf: false, created_at: '2024-02-15T09:00:00Z', metadata: null },
  { id: 'sc-inv-003', invoice_number: 'INV-1044', invoice_type: 'standard', status: 'draft', currency: 'AUD', subtotal_cents: 300000, tax_cents: 30000, credit_applied_cents: 0, total_cents: 330000, period_start: '2024-02-01', period_end: '2024-02-28', issued_at: null, due_at: '2024-03-28T00:00:00Z', paid_at: null, customer_id: 'sc-cust-003', has_pdf: false, created_at: '2024-02-28T11:00:00Z', metadata: null },
]

// ─── Quotes ────────────────────────────────────────────────

const SHOWCASE_QUOTES = [
  { id: 'sc-qt-001', quote_number: 'QT-0010', customer_id: 'sc-cust-001', customer_name: 'Oakridge Developments', status: 'accepted', currency: 'AUD', subtotal_cents: 800000, tax_cents: 80000, total_cents: 880000, reference: null, notes: null, issued_at: '2024-02-08T10:00:00Z', expires_at: '2024-03-08T00:00:00Z', accepted_at: '2024-02-10T00:00:00Z', created_at: '2024-02-08T10:00:00Z' },
  { id: 'sc-qt-002', quote_number: 'QT-0011', customer_id: 'sc-cust-003', customer_name: 'Coastal Property Partners', status: 'draft', currency: 'AUD', subtotal_cents: 400000, tax_cents: 40000, total_cents: 440000, reference: null, notes: null, issued_at: null, expires_at: '2024-03-25T00:00:00Z', accepted_at: null, created_at: '2024-02-25T09:00:00Z' },
]

// ─── Purchase Orders ───────────────────────────────────────

const SHOWCASE_PURCHASE_ORDERS = [
  { id: 'sc-po-001', po_number: 'PO-0050', supplier_id: 'sc-sup-001', supplier_name: 'Metro Office Supplies', status: 'sent' as const, currency: 'AUD', subtotal_cents: 42000, tax_cents: 4200, total_cents: 46200, reference: null, notes: null, issued_at: '2024-02-12T11:00:00Z', expected_delivery_at: '2024-03-01T00:00:00Z', created_at: '2024-02-12T11:00:00Z' },
  { id: 'sc-po-002', po_number: 'PO-0051', supplier_id: 'sc-sup-002', supplier_name: 'Pacific Utilities Co', status: 'received' as const, currency: 'AUD', subtotal_cents: 120000, tax_cents: 12000, total_cents: 132000, reference: null, notes: null, issued_at: '2024-02-18T09:00:00Z', expected_delivery_at: '2024-02-25T00:00:00Z', created_at: '2024-02-18T09:00:00Z' },
]

// ─── Credit Notes ──────────────────────────────────────────

const SHOWCASE_CREDIT_NOTES = [
  { id: 'sc-cn-001', credit_note_number: 'CN-0003', credit_note_type: 'customer' as const, status: 'issued' as const, currency: 'AUD', subtotal_cents: 50000, tax_cents: 5000, total_cents: 55000, remaining_cents: 55000, customer_id: 'sc-cust-002', customer_name: 'Summit Advisory Group', supplier_id: null, supplier_name: null, reference: null, notes: null, issued_at: '2024-02-22T14:00:00Z', created_at: '2024-02-22T14:00:00Z' },
]

// ─── Jobs ──────────────────────────────────────────────────

const SHOWCASE_JOBS = [
  { id: 'sc-job-001', name: 'Oakridge Fitout', code: 'J-001', status: 'active', customer_id: 'sc-cust-001', customer_name: 'Oakridge Developments', budget_cents: 1500000, currency: 'AUD', created_at: '2024-01-20T00:00:00Z' },
  { id: 'sc-job-002', name: 'Summit Annual Audit', code: 'J-002', status: 'active', customer_id: 'sc-cust-002', customer_name: 'Summit Advisory Group', budget_cents: 800000, currency: 'AUD', created_at: '2024-02-01T00:00:00Z' },
]

// ─── Expense Claims ────────────────────────────────────────

const SHOWCASE_EXPENSE_CLAIMS = [
  { id: 'sc-ec-001', claim_number: 'EC-001', status: 'approved' as const, currency: 'AUD', total_cents: 12500, description: 'Client lunch - Oakridge', claimant_id: USER_ID, claimant_name: 'Alex Morgan', submitted_at: '2024-02-14T12:00:00Z', approved_at: '2024-02-15T09:00:00Z', created_at: '2024-02-14T12:00:00Z' },
]

// ─── Time Entries ──────────────────────────────────────────

const SHOWCASE_TIME_ENTRIES = [
  { id: 'sc-te-001', job_id: 'sc-job-001', job_code: 'J-001', job_name: 'Oakridge Fitout', user_id: USER_ID, user_name: 'Alex Morgan', date: '2024-02-19', hours: 3.5, description: 'Site visit - measurements', billable: true, hourly_rate_cents: 15000, amount_cents: 52500, status: 'approved' as const, invoice_line_item_id: null, created_at: '2024-02-19T17:00:00Z' },
  { id: 'sc-te-002', job_id: 'sc-job-002', job_code: 'J-002', job_name: 'Summit Annual Audit', user_id: USER_ID, user_name: 'Alex Morgan', date: '2024-02-20', hours: 5.0, description: 'Audit preparation', billable: true, hourly_rate_cents: 18000, amount_cents: 90000, status: 'approved' as const, invoice_line_item_id: null, created_at: '2024-02-20T17:00:00Z' },
]

// ─── Inventory Items ───────────────────────────────────────

const SHOWCASE_INVENTORY_ITEMS = [
  { id: 'sc-item-001', name: 'Consulting - Standard', sku: 'CONS-STD', base_unit: 'hour', pack_unit: null, pack_to_base_quantity: 1, on_hand_quantity: 0, average_unit_cost_cents: 0, currency: 'AUD', reorder_point_quantity: null, low_stock: false, active: true, stock_value_cents: 0, created_at: '2024-01-18T00:00:00Z' },
  { id: 'sc-item-002', name: 'Project Management - Hourly', sku: 'PM-HR', base_unit: 'hour', pack_unit: null, pack_to_base_quantity: 1, on_hand_quantity: 0, average_unit_cost_cents: 0, currency: 'AUD', reorder_point_quantity: null, low_stock: false, active: true, stock_value_cents: 0, created_at: '2024-01-18T00:00:00Z' },
]

// ─── Features ──────────────────────────────────────────────

const SHOWCASE_FEATURES = {
  release_channel: 'stable' as const,
  features: {
    onboarding_import_myob: false,
    onboarding_import_xero: false,
    classic_mode: true,
    invoicing: true,
    banking: true,
    inventory: true,
    timesheets: true,
    expense_claims: true,
    purchase_orders: true,
    fixed_assets: true,
    budgets: true,
    jobs: true,
    multi_currency: true,
    portal: true,
    utilities: true,
  },
}

// ─── Route → fixture mapping ──────────────────────────────

const ROUTE_MAP: Record<string, any> = {}

function buildRouteMap(): void {
  // Auth / user
  ROUTE_MAP['/auth/me'] = SHOWCASE_USER

  // Global setup status - tells the app provisioning is complete
  ROUTE_MAP['/auth/setup-status'] = {
    needs_setup: false,
    provisioned: true,
    oauth_configured: true,
    frontend_url_configured: true,
    user_count: 1,
  }

  // Readiness - tells the app the system is operational
  ROUTE_MAP['/readiness'] = {
    state: 'ready' as const,
    ready: true,
    summary: 'All systems operational',
    timestamp: NOW,
  }

  // Heartbeat - will only be hit if the path goes through request()
  ROUTE_MAP['/auth/heartbeat'] = {
    session_expires_at: new Date(Date.now() + 86400000).toISOString(),
    online: true,
  }

  // Organisation - wrapped for getOrganisation() which accesses data.organisation
  ROUTE_MAP[`/organisations/${ORG_ID}`] = { organisation: SHOWCASE_ORG }
  ROUTE_MAP[`/organisations/${ORG_ID}/features`] = SHOWCASE_FEATURES

  // Organisations list - getOrganisations() has Array.isArray fallback
  ROUTE_MAP['/organisations'] = [SHOWCASE_ORG]

  // Accounts - getAccounts() has Array.isArray fallback, raw array is fine
  ROUTE_MAP[`/organisations/${ORG_ID}/accounts`] = SHOWCASE_ACCOUNTS

  // Journal entries - getJournalEntries() has Array.isArray fallback
  ROUTE_MAP[`/organisations/${ORG_ID}/journal_entries`] = SHOWCASE_JOURNALS

  // Contacts - wrapped for getCustomers()/getSuppliers() which access data.customers/data.suppliers
  ROUTE_MAP[`/organisations/${ORG_ID}/customers`] = { customers: SHOWCASE_CUSTOMERS }
  ROUTE_MAP[`/organisations/${ORG_ID}/suppliers`] = { suppliers: SHOWCASE_SUPPLIERS }

  // Invoices - wrapped for getInvoices() which returns InvoicesResponse
  ROUTE_MAP[`/organisations/${ORG_ID}/invoices`] = { invoices: SHOWCASE_INVOICES, pagination: { page: 1, per_page: 25, total: SHOWCASE_INVOICES.length, total_pages: 1 } }

  // Quotes - wrapped for getQuotes() which returns { quotes: [] }
  ROUTE_MAP[`/organisations/${ORG_ID}/quotes`] = { quotes: SHOWCASE_QUOTES }

  // Purchase orders - wrapped for getPurchaseOrders() which returns { purchase_orders: [] }
  ROUTE_MAP[`/organisations/${ORG_ID}/purchase_orders`] = { purchase_orders: SHOWCASE_PURCHASE_ORDERS }

  // Credit notes - wrapped for getCreditNotes() which returns { credit_notes: [] }
  ROUTE_MAP[`/organisations/${ORG_ID}/credit_notes`] = { credit_notes: SHOWCASE_CREDIT_NOTES }

  // Jobs - wrapped for getJobs() which returns { jobs: [] }
  ROUTE_MAP[`/organisations/${ORG_ID}/jobs`] = { jobs: SHOWCASE_JOBS }

  // Expense claims - wrapped for getExpenseClaims() which returns { expense_claims: [], pagination }
  ROUTE_MAP[`/organisations/${ORG_ID}/expense_claims`] = { expense_claims: SHOWCASE_EXPENSE_CLAIMS, pagination: { page: 1, per_page: 25, total: SHOWCASE_EXPENSE_CLAIMS.length, total_pages: 1 } }

  // Time entries - wrapped for getTimeEntries() which returns TimeEntriesResponse
  ROUTE_MAP[`/organisations/${ORG_ID}/time_entries`] = { time_entries: SHOWCASE_TIME_ENTRIES, pagination: { page: 1, per_page: 25, total: SHOWCASE_TIME_ENTRIES.length, total_pages: 1 } }

  // Inventory items - getInventoryItems() calls /inventory/items and accesses data.items
  ROUTE_MAP[`/organisations/${ORG_ID}/inventory/items`] = { items: SHOWCASE_INVENTORY_ITEMS }

  // Billing profile (used by PurchaseOrdersPage and others)
  ROUTE_MAP[`/organisations/${ORG_ID}/billing_profile`] = { currency: 'AUD', tax_label: 'GST', tax_rate: 0.1 }

  // Fixed assets - empty wrapper
  ROUTE_MAP[`/organisations/${ORG_ID}/fixed_assets`] = { fixed_assets: [] }

  // Bank reconciliations - empty wrapper
  ROUTE_MAP[`/organisations/${ORG_ID}/bank_reconciliations`] = { reconciliations: [] }

  // Bank transactions - empty wrapper
  ROUTE_MAP[`/organisations/${ORG_ID}/bank_transactions`] = { bank_transactions: [] }

  // Bills - empty wrapper
  ROUTE_MAP[`/organisations/${ORG_ID}/bills`] = { bills: [] }

  // Inventory movements - getInventoryMovements() unwraps data.movements
  ROUTE_MAP[`/organisations/${ORG_ID}/inventory/movements`] = { movements: [] }

  // Bank rules - getBankRules() pass-through, page reads res.bank_rules
  ROUTE_MAP[`/organisations/${ORG_ID}/bank_rules`] = { bank_rules: [] }

  // Budgets - getBudgets() pass-through, page reads res.budgets
  ROUTE_MAP[`/organisations/${ORG_ID}/budgets`] = { budgets: [] }

  // Tracking categories - getTrackingCategories() pass-through
  ROUTE_MAP[`/organisations/${ORG_ID}/tracking_categories`] = { tracking_categories: [] }

  // Utility accounts - getUtilityAccounts() pass-through, page reads res.utility_accounts
  ROUTE_MAP[`/organisations/${ORG_ID}/utility_accounts`] = { utility_accounts: [], pagination: { page: 1, per_page: 25, total: 0, total_pages: 1 } }

  // Utility readings - needed by BillComparisonPage
  ROUTE_MAP[`/organisations/${ORG_ID}/utility_readings`] = { utility_readings: [], pagination: { page: 1, per_page: 25, total: 0, total_pages: 1 } }

  // Executive dashboard report - getExecutiveDashboard() pass-through
  ROUTE_MAP[`/organisations/${ORG_ID}/reports/executive_dashboard`] = {
    report: 'executive_dashboard',
    organisation_id: ORG_ID,
    as_of: NOW,
    ytd: {
      revenue: { total_cents: 18450000, change_pct: 12.4 },
      expense: { total_cents: 11230000, change_pct: 8.1 },
      net_income: { total_cents: 7220000, change_pct: 18.6 },
    },
    mtd: {
      revenue: { total_cents: 2640000, change_pct: 5.2 },
      expense: { total_cents: 1580000, change_pct: -3.1 },
      net_income: { total_cents: 1060000, change_pct: 14.8 },
    },
    cash_position: { total_cents: 4250000 },
    receivables_cents: 3120000,
    payables_cents: 1890000,
    overdue_invoices: 2,
    overdue_bills: 1,
  }

  // Wallet - getWallet() uses /wallet (no org prefix)
  ROUTE_MAP['/wallet'] = {
    balance_cents: 0,
    balance_dollars: 0,
    card_verified: false,
    topup_cap_cents: null,
    transactions: [],
  }

  // Wallet transactions - getWalletTransactions()
  ROUTE_MAP['/wallet/transactions'] = { transactions: [], page: 1, per_page: 25, total: 0 }

  // API tokens - getApiTokens() has smart unwrap (Array.isArray fallback)
  ROUTE_MAP[`/organisations/${ORG_ID}/api_tokens`] = []

  // Audit logs - getAuditLogs() pass-through
  ROUTE_MAP[`/organisations/${ORG_ID}/audit_logs`] = { audit_logs: [], pagination: { page: 1, per_page: 25, total: 0, total_pages: 1 } }

  // Invoice templates (repeating invoices) - getInvoiceTemplates() unwraps data.invoice_templates
  ROUTE_MAP[`/organisations/${ORG_ID}/invoice_templates`] = { invoice_templates: [] }

  // SLA credits - getSlaCredits() pass-through
  ROUTE_MAP[`/organisations/${ORG_ID}/sla_credits`] = { sla_credits: [], summary: { total_cents: 0, used_cents: 0, remaining_cents: 0, expired_cents: 0 } }

  // Contact groups - getContactGroups() pass-through
  ROUTE_MAP[`/organisations/${ORG_ID}/contact_groups`] = { contact_groups: [] }

  // Setup / provisioning - make it report fully provisioned
  ROUTE_MAP[`/organisations/${ORG_ID}/setup_status`] = { provisioned: true, state: 'ready' }
  ROUTE_MAP[`/organisations/${ORG_ID}/provisioning`] = { provisioned: true, state: 'ready', steps: [] }

  // Sequences - return empty reservations
  ROUTE_MAP[`/organisations/${ORG_ID}/sequence_reservations`] = { reservations: {} }
}

/**
 * Resolve a showcase API fixture for the given path.
 * Returns the fixture data or undefined if no fixture exists
 * (in which case a sensible empty fallback is returned).
 */
export function showcaseRequest(path: string, method: string): any {
  if (Object.keys(ROUTE_MAP).length === 0) buildRouteMap()

  // Writes in showcase mode are silently accepted
  if (method !== 'GET' && method !== 'HEAD') {
    return { ok: true }
  }

  // Strip query parameters before matching
  const cleanPath = path.split('?')[0]

  // Exact match first
  if (ROUTE_MAP[cleanPath] !== undefined) return ROUTE_MAP[cleanPath]

  // Prefix match for detail routes (e.g., /organisations/sc-org-001/accounts/sc-acc-001)
  // Sort keys by length descending so the longest (most specific) prefix wins
  const keys = Object.keys(ROUTE_MAP).sort((a, b) => b.length - a.length)
  for (const key of keys) {
    if (cleanPath.startsWith(key + '/')) {
      const remainder = cleanPath.slice(key.length + 1)
      // Only match single-segment remainders (an ID), not deeper sub-paths
      if (remainder.includes('/')) continue

      const value = ROUTE_MAP[key]
      const id = remainder

      // Raw array (accounts, journal_entries, organisations list)
      if (Array.isArray(value)) {
        const item = value.find((r: { id: string }) => r.id === id)
        if (item) return item
      }

      // Wrapped collection - look for the first array value inside the envelope
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        for (const arr of Object.values(value)) {
          if (Array.isArray(arr)) {
            const item = (arr as { id: string }[]).find((r) => r.id === id)
            if (item) return item
          }
        }
      }
    }
  }

  // Default: return an empty array for unknown GET collection routes
  return []
}

/**
 * Seed the OfflineStore with all showcase fixtures so the app
 * can bootstrap and render without hitting any real API.
 */
export function seedShowcaseCache(store: OfflineStore): void {
  if (Object.keys(ROUTE_MAP).length === 0) buildRouteMap()

  for (const [path, data] of Object.entries(ROUTE_MAP)) {
    store.setCache(path, data)
  }

  // Also seed the org ID into localStorage so orgId() resolution works
  localStorage.setItem('ledger_core_org', ORG_ID)
}
